/**
 * Strategy Actions Menu Component
 *
 * Displays action buttons for strategy management:
 * - Emergency Clear (danger)
 * - Pause/Resume Monitoring
 * - Reload Snapshot
 * - Update Budget
 * - Update Profit Tiers
 * - Update Hedge Config
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TuneIcon from '@mui/icons-material/Tune';
import PercentIcon from '@mui/icons-material/Percent';
import RepeatIcon from '@mui/icons-material/Repeat';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningIcon from '@mui/icons-material/Warning';
import FastForwardIcon from '@mui/icons-material/FastForward';
import { traderTheme as theme } from './traderDashboardTheme';

export type StrategyAction =
  | 'view_details'
  | 'emergency_clear'
  | 'pause'
  | 'resume'
  | 'reload'
  | 'skip'
  | 'exclude'
  | 'update_budget'
  | 'update_entry_threshold'
  | 'update_profit_tiers'
  | 'update_replay_settings'
  | 'update_entry_funding_rate'
  | 'update_hedge_config';

export interface StrategyActionsMenuProps {
  strategyId: string;
  /** Current strategy state */
  currentState?: string;
  /** Whether monitoring is currently paused */
  isMonitoringPaused?: boolean;
  /** Whether the strategy is in a state that supports actions */
  supportsActions?: boolean;
  /** Whether replay settings can be edited for this strategy */
  supportsReplaySettings?: boolean;
  /** Whether BuyOpen entry funding threshold can be edited for this strategy */
  supportsEntryFundingRateEdit?: boolean;
  /** Loading state for each action type */
  loadingAction?: StrategyAction | null;
  /** Callback when an action is triggered */
  onAction: (strategyId: string, action: StrategyAction) => void;
}

export const StrategyActionsMenu: React.FC<StrategyActionsMenuProps> = ({
  strategyId,
  currentState,
  isMonitoringPaused = false,
  supportsActions = true,
  supportsReplaySettings = false,
  supportsEntryFundingRateEdit = false,
  loadingAction = null,
  onAction,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: StrategyAction) => {
    handleClose();
    onAction(strategyId, action);
  };

  const isLoading = loadingAction !== null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {/* Actions Menu */}
      <Tooltip title={t('traderDashboard.actions.more')} arrow placement="top">
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={isLoading}
          sx={{
            color: theme.text.secondary,
            '&:hover': {
              backgroundColor: 'rgba(88, 166, 255, 0.15)',
              color: theme.accent,
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} sx={{ color: theme.accent }} />
          ) : (
            <MoreVertIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
            minWidth: 180,
          },
        }}
      >
        {/* View Details */}
        <MenuItem
          onClick={() => handleAction('view_details')}
          sx={{
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.bg.hover,
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('traderDashboard.actions.viewDetails')}
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>

        <Divider sx={{ borderColor: theme.border }} />

        {/* Update Budget */}
        <MenuItem
          onClick={() => handleAction('update_budget')}
          disabled={!supportsActions}
          sx={{
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.bg.hover,
            },
            '&.Mui-disabled': {
              color: theme.text.secondary,
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            {loadingAction === 'update_budget' ? (
              <CircularProgress size={18} sx={{ color: theme.profit }} />
            ) : (
              <AttachMoneyIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={t('traderDashboard.actions.updateBudget')}
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>

        {(currentState === 'BuyOpen' || currentState === 'Standby') && (
          <MenuItem
            onClick={() => handleAction('update_entry_threshold')}
            disabled={!supportsActions}
            sx={{
              color: theme.text.primary,
              '&:hover': { backgroundColor: theme.bg.hover },
              '&.Mui-disabled': { color: theme.text.secondary },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              {loadingAction === 'update_entry_threshold' ? (
                <CircularProgress size={18} sx={{ color: theme.accent }} />
              ) : (
                <PercentIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t('traderDashboard.actions.updateEntryThreshold')}
              primaryTypographyProps={{ fontSize: '0.85rem' }}
            />
          </MenuItem>
        )}

        {supportsEntryFundingRateEdit && (
          <MenuItem
            onClick={() => handleAction('update_entry_funding_rate')}
            disabled={!supportsActions}
            sx={{
              color: theme.text.primary,
              '&:hover': {
                backgroundColor: theme.bg.hover,
              },
              '&.Mui-disabled': {
                color: theme.text.secondary,
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              {loadingAction === 'update_entry_funding_rate' ? (
                <CircularProgress size={18} sx={{ color: theme.accent }} />
              ) : (
                <PercentIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t('traderDashboard.actions.updateEntryFundingRate')}
              primaryTypographyProps={{ fontSize: '0.85rem' }}
            />
          </MenuItem>
        )}

        {/* Update Profit Tiers */}
        <MenuItem
          onClick={() => handleAction('update_profit_tiers')}
          disabled={!supportsActions}
          sx={{
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.bg.hover,
            },
            '&.Mui-disabled': {
              color: theme.text.secondary,
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            {loadingAction === 'update_profit_tiers' ? (
              <CircularProgress size={18} sx={{ color: theme.accent }} />
            ) : (
              <TuneIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={t('traderDashboard.actions.updateProfitTiers')}
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>

        {supportsReplaySettings && (
          <MenuItem
            onClick={() => handleAction('update_replay_settings')}
            disabled={!supportsActions}
            sx={{
              color: theme.text.primary,
              '&:hover': {
                backgroundColor: theme.bg.hover,
              },
              '&.Mui-disabled': {
                color: theme.text.secondary,
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              {loadingAction === 'update_replay_settings' ? (
                <CircularProgress size={18} sx={{ color: theme.accent }} />
              ) : (
                <RepeatIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t('traderDashboard.actions.updateReplaySettings')}
              primaryTypographyProps={{ fontSize: '0.85rem' }}
            />
          </MenuItem>
        )}

        {/* Update Hedge Config */}
        <MenuItem
          onClick={() => handleAction('update_hedge_config')}
          disabled={!supportsActions}
          sx={{
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.bg.hover,
            },
            '&.Mui-disabled': {
              color: theme.text.secondary,
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            {loadingAction === 'update_hedge_config' ? (
              <CircularProgress size={18} sx={{ color: theme.accent }} />
            ) : (
              <WarningIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={t('traderDashboard.actions.updateHedgeConfig')}
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>

        {/* Reload Snapshot */}
        <MenuItem
          onClick={() => handleAction('reload')}
          disabled={!supportsActions}
          sx={{
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.bg.hover,
            },
            '&.Mui-disabled': {
              color: theme.text.secondary,
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            {loadingAction === 'reload' ? (
              <CircularProgress size={18} sx={{ color: theme.accent }} />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={t('traderDashboard.actions.reload')}
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>

        {/* Skip BuyOpen - only visible in BuyOpen state */}
        {currentState === 'BuyOpen' && (
          <MenuItem
            onClick={() => handleAction('skip')}
            disabled={!supportsActions}
            sx={{
              color: theme.warning,
              '&:hover': {
                backgroundColor: 'rgba(210, 153, 34, 0.15)',
              },
              '&.Mui-disabled': {
                color: theme.text.secondary,
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              {loadingAction === 'skip' ? (
                <CircularProgress size={18} sx={{ color: theme.warning }} />
              ) : (
                <FastForwardIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t('traderDashboard.actions.skip')}
              primaryTypographyProps={{ fontSize: '0.85rem' }}
            />
          </MenuItem>
        )}

        {/* Pause/Resume Monitoring */}
        <MenuItem
          onClick={() => handleAction(isMonitoringPaused ? 'resume' : 'pause')}
          disabled={!supportsActions}
          sx={{
            color: isMonitoringPaused ? theme.profit : theme.warning,
            '&:hover': {
              backgroundColor: isMonitoringPaused
                ? 'rgba(63, 185, 80, 0.15)'
                : 'rgba(210, 153, 34, 0.15)',
            },
            '&.Mui-disabled': {
              color: theme.text.secondary,
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            {loadingAction === 'pause' || loadingAction === 'resume' ? (
              <CircularProgress size={18} sx={{ color: theme.warning }} />
            ) : isMonitoringPaused ? (
              <PlayCircleOutlineIcon fontSize="small" />
            ) : (
              <PauseCircleOutlineIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={isMonitoringPaused
              ? t('traderDashboard.actions.resume')
              : t('traderDashboard.actions.pause')}
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>

        {isMonitoringPaused && (
          <MenuItem
            onClick={() => handleAction('exclude')}
            sx={{
              color: theme.text.secondary,
              '&:hover': {
                backgroundColor: theme.bg.hover,
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              {loadingAction === 'exclude' ? (
                <CircularProgress size={18} sx={{ color: theme.text.secondary }} />
              ) : (
                <VisibilityOffIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t('traderDashboard.actions.exclude')}
              primaryTypographyProps={{ fontSize: '0.85rem' }}
            />
          </MenuItem>
        )}

        <Divider sx={{ borderColor: theme.border }} />

        {/* Emergency Clear (danger action at bottom) */}
        <MenuItem
          onClick={() => handleAction('emergency_clear')}
          sx={{
            color: theme.danger,
            '&:hover': {
              backgroundColor: 'rgba(248, 81, 73, 0.15)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <DeleteForeverIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('traderDashboard.actions.emergencyClear')}
            primaryTypographyProps={{ fontSize: '0.85rem' }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default StrategyActionsMenu;
